import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Ticket, Clock, CheckCircle, XCircle, Warning, Plus } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/constants";

const STATUS_META = {
  active:   { color: "bg-emerald-50 border-emerald-700 text-emerald-800", label: "Active",   Icon: CheckCircle },
  redeemed: { color: "bg-zinc-100 border-zinc-500 text-zinc-700",         label: "Redeemed", Icon: Ticket },
  expired:  { color: "bg-yellow-50 border-yellow-600 text-yellow-800",    label: "Expired",  Icon: Clock },
  void:     { color: "bg-red-50 border-red-700 text-red-800",             label: "Void",     Icon: XCircle },
};

export default function ReplacementVouchers() {
  const { user, hasPerm } = useAuth();
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState({ active: 0, redeemed: 0, expired: 0, void: 0 });
  const [filterStatus, setFilterStatus] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [clients, setClients] = useState([]);
  const [showManual, setShowManual] = useState(false);
  const [form, setForm] = useState({ client_id: "", campaign_id: "", expires_days: 90, notes: "" });
  const [campaigns, setCampaigns] = useState([]);

  const isInternal = user?.org_type === "internal";

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (clientFilter) params.set("client_id", clientFilter);
      const [{ data: v }, { data: s }] = await Promise.all([
        api.get(`/replacement-vouchers?${params.toString()}`),
        api.get(`/replacement-vouchers/summary${clientFilter ? `?client_id=${clientFilter}` : ""}`),
      ]);
      setRows(v.vouchers || []);
      setSummary(s);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
      setRows([]);
    }
  }, [filterStatus, clientFilter]);

  useEffect(() => {
    if (isInternal) {
      api.get("/organizations?type=client").then((r) => setClients(r.data.organizations || []));
      api.get("/campaigns").then((r) => setCampaigns(r.data.campaigns || []));
    }
  }, [isInternal]);

  useEffect(() => { load(); }, [load]);

  // Campaign dropdown filter — memoized so it doesn't recompute on every render.
  const filteredCampaigns = useMemo(
    () => campaigns.filter((c) => !form.client_id || c.client_id === form.client_id),
    [campaigns, form.client_id],
  );

  const voidVoucher = async (id) => {
    const reason = prompt("Reason for voiding? (optional)") ?? "";
    try {
      await api.post(`/replacement-vouchers/${id}/void`, { reason });
      toast.success("Voided");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const extend = async (id) => {
    const days = Number(prompt("Extend by how many days?", "30") || 0);
    if (!days) return;
    try {
      await api.post(`/replacement-vouchers/${id}/extend`, { days });
      toast.success(`Extended by ${days} days`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const manualSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/replacement-vouchers", {
        client_id: form.client_id,
        campaign_id: form.campaign_id || null,
        expires_days: Number(form.expires_days) || 90,
        notes: form.notes || null,
        source_bad_lead_id: "manual",
      });
      toast.success("Voucher issued");
      setShowManual(false);
      setForm({ client_id: "", campaign_id: "", expires_days: 90, notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (user?.org_type === "affiliate") {
    return (
      <div className="brut-card p-6" data-testid="vouchers-denied">
        <div className="label-mono">Replacement vouchers</div>
        <div className="mt-1 font-chivo font-black text-2xl uppercase">Not available for affiliates</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-mono">Bad-lead resolutions</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">
            Replacement vouchers
          </h1>
          <div className="mt-1 text-sm text-zinc-600 max-w-2xl">
            Vouchers issued in lieu of a monetary credit. The next matching lead we intake for the client
            {" "}(same campaign if scoped) auto-redeems the voucher and is billed at $0.
          </div>
        </div>
        {isInternal && hasPerm("review_bad_leads") && (
          <button onClick={() => setShowManual((v) => !v)} data-testid="voucher-new-btn"
            className="border border-black bg-black text-white px-3 py-2 text-xs uppercase tracking-widest hover:bg-yellow-500 hover:text-black">
            <Plus size={12} className="inline mr-1" /> Issue voucher
          </button>
        )}
      </div>

      <div className="grid grid-cols-4 gap-3 mb-4">
        {Object.entries(summary).map(([k, v]) => {
          const meta = STATUS_META[k] || {};
          const Icon = meta.Icon || Ticket;
          return (
            <div key={k} className={`brut-card p-4 ${meta.color}`} data-testid={`voucher-summary-${k}`}>
              <div className="flex items-center justify-between">
                <div className="label-mono">{meta.label || k}</div>
                <Icon size={14} weight="bold" />
              </div>
              <div className="font-chivo font-black text-3xl mt-1">{v}</div>
            </div>
          );
        })}
      </div>

      {showManual && isInternal && (
        <form onSubmit={manualSubmit} className="brut-card p-4 mb-4 grid grid-cols-2 gap-3" data-testid="voucher-manual-form">
          <div>
            <label className="label-mono">Client*</label>
            <select required data-testid="voucher-client" className="w-full border border-black px-2 py-1.5 mt-1"
              value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
              <option value="">— select —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-mono">Campaign (optional)</label>
            <select className="w-full border border-black px-2 py-1.5 mt-1"
              value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })}>
              <option value="">Any campaign</option>
              {filteredCampaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-mono">Expires in (days)</label>
            <input type="number" min={1} max={365} data-testid="voucher-expires"
              className="w-full border border-black px-2 py-1.5 mt-1 font-mono"
              value={form.expires_days}
              onChange={(e) => setForm({ ...form, expires_days: e.target.value })} />
          </div>
          <div>
            <label className="label-mono">Notes</label>
            <input className="w-full border border-black px-3 py-2 mt-1"
              value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="col-span-2 flex justify-end">
            <button type="submit" data-testid="voucher-submit"
              className="bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold">
              Issue voucher
            </button>
          </div>
        </form>
      )}

      <div className="flex gap-2 mb-3">
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="voucher-status-filter"
          className="border border-black px-2 py-1.5 font-mono text-xs">
          <option value="">All statuses</option>
          {Object.keys(STATUS_META).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        {isInternal && (
          <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
            data-testid="voucher-client-filter"
            className="border border-black px-2 py-1.5 font-mono text-xs">
            <option value="">All clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
      </div>

      <div className="brut-card overflow-x-auto">
        <table className="w-full text-xs font-mono min-w-[900px]">
          <thead>
            <tr className="border-b border-black text-[10px] uppercase tracking-widest">
              <th className="py-2 px-3 text-left">Status</th>
              <th className="py-2 px-3 text-left">Client / campaign</th>
              <th className="py-2 px-3 text-left">Source</th>
              <th className="py-2 px-3 text-left">Created</th>
              <th className="py-2 px-3 text-left">Expires</th>
              <th className="py-2 px-3 text-left">Redeemed</th>
              <th className="py-2 px-3 text-left"></th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((v) => {
              const m = STATUS_META[v.status] || {};
              const client = clients.find((c) => c.id === v.client_id);
              const camp = campaigns.find((c) => c.id === v.campaign_id);
              return (
                <tr key={v.id} className="border-b border-zinc-200" data-testid={`voucher-row-${v.id}`}>
                  <td className="py-2 px-3">
                    <span className={`inline-block px-2 py-0.5 border ${m.color || ""}`}>
                      {m.label || v.status}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <div className="font-semibold">{client?.name || v.client_id.slice(0, 8)}</div>
                    <div className="text-[10px] text-zinc-500">
                      {camp ? camp.name : (v.campaign_id ? v.campaign_id.slice(0, 8) : "any campaign")}
                    </div>
                  </td>
                  <td className="py-2 px-3 text-[11px]">
                    {v.source_bad_lead_id === "manual" ? "manual" : (
                      <Link to={`/bad-leads/${v.source_bad_lead_id}`} className="hover:underline"
                            data-testid={`voucher-source-${v.id}`}>
                        {(v.source_bad_lead_id || "").slice(0, 8)}
                      </Link>
                    )}
                  </td>
                  <td className="py-2 px-3">{fmtDate(v.created_at)}</td>
                  <td className="py-2 px-3">{fmtDate(v.expires_at)}</td>
                  <td className="py-2 px-3">
                    {v.redeemed_by_lead_id ? (
                      <Link to={`/leads/${v.redeemed_by_lead_id}`} className="hover:underline"
                            data-testid={`voucher-redeem-${v.id}`}>
                        {v.redeemed_by_lead_id.slice(0, 8)}
                      </Link>
                    ) : "—"}
                  </td>
                  <td className="py-2 px-3">
                    {v.status === "active" && isInternal && hasPerm("review_bad_leads") && (
                      <>
                        <button onClick={() => extend(v.id)} data-testid={`voucher-extend-${v.id}`}
                          className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white mr-1">
                          Extend
                        </button>
                        <button onClick={() => voidVoucher(v.id)} data-testid={`voucher-void-${v.id}`}
                          className="border border-red-600 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-600 hover:text-white">
                          Void
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows !== null && rows.length === 0 && (
              <tr><td colSpan={7} className="py-6 text-center label-mono">No vouchers found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
